/**
 * Renders a block's hazard rows (read-only) as a stack of per-hazard cards.
 * Mirrors the visual language of RiskAssessmentDetail.js so the library /
 * template-editor read-only views feel like the same product as the
 * normal-user RA edit view: each card has a header (title + inline
 * RiskReductionBar) and a 3-panel body (Initial / Controls / Residual).
 *
 * Used by:
 *   - RiskAssessmentLibrary.js (Blocks tab expanded view)
 *   - RiskAssessmentTemplateEditor.js (per-block expanded view)
 *
 * Restructured from a desktop-table + mobile-card split to a unified
 * card-per-hazard layout on 2026-05-23 — see the F-5 plan in
 * `~/.claude/plans/please-look-at-the-vast-elephant.md`.
 */
import React from 'react';
import {
  computeHrn, bandFor, BAND_CLASSES, labelFor, formatHrnPill,
  LO_SCALE, FE_SCALE, DPH_SCALE, NP_SCALE,
} from '../constants/riskAssessment';
import ControlsByStage from './ControlsByStage';
import PpePicker from './PpePicker';
import RegsSelector from './RegsSelector';
import RiskReductionBar from './RiskReductionBar';

export function HrnReadout({ lo, fe, dph, np, band, hrn }) {
  return (
    <div className="space-y-1 min-w-[10rem]">
      <span
        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${BAND_CLASSES[band.tone]}`}
        title={`LO ${lo} × FE ${fe} × DPH ${dph} × NP ${np}`}
      >
        {formatHrnPill(hrn)}
      </span>
      <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-snug">
        <div>LO {lo} · {labelFor(LO_SCALE, lo)}</div>
        <div>FE {fe} · {labelFor(FE_SCALE, fe)}</div>
        <div>DPH {dph} · {labelFor(DPH_SCALE, dph)}</div>
        <div>NP {np} · {labelFor(NP_SCALE, np)}</div>
      </div>
    </div>
  );
}

export default function BlockRowsTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <p className="py-3 text-sm text-gray-500 dark:text-gray-400">No hazard rows yet.</p>;
  }
  return (
    <div className="mt-3 space-y-3">
      {rows.map((r) => {
        const hrnInit = computeHrn(r.lo_init, r.fe_init, r.dph_init, r.np_init);
        const hrnRes  = computeHrn(r.lo_res,  r.fe_res,  r.dph_res,  r.np_res);
        const bandInit = bandFor(hrnInit);
        const bandRes  = bandFor(hrnRes);
        const hazardTitle = r.hazard || '';
        // Mirror RiskAssessmentDetail.js: short titles get the bar inline on
        // desktop, long titles drop it to a below-title row. The 76-char
        // breakpoint is tuned to "Gimbal / motion platform failure causing
        // fall or crush" fitting alongside the bar without wrapping.
        const isLongTitle = hazardTitle.length > 76;
        return (
          <div
            key={r.id}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3"
          >
            {/* Header — title + inline bar on desktop short-title path */}
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {hazardTitle}
              </h3>
              {!isLongTitle && (
                <div className="hidden lg:block flex-1 min-w-[160px] -mt-3">
                  <RiskReductionBar initialHrn={hrnInit} residualHrn={hrnRes} />
                </div>
              )}
            </div>

            {/* Below-title bar — mobile always, desktop only when title is long */}
            <div className={isLongTitle ? '' : 'lg:hidden'}>
              <RiskReductionBar initialHrn={hrnInit} residualHrn={hrnRes} />
            </div>

            {/* Who is affected */}
            {r.who_affected && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  Who is affected
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {r.who_affected}
                </div>
              </div>
            )}

            {/* Body — Initial / Controls / Residual 3-panel layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  Initial risk
                </div>
                <HrnReadout
                  lo={r.lo_init} fe={r.fe_init} dph={r.dph_init} np={r.np_init}
                  band={bandInit} hrn={hrnInit}
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  Controls
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                  <ControlsByStage value={r.controls} readOnly compact />
                  <PpePicker value={r.ppe_list} readOnly />
                  <RegsSelector value={r.regs_list} readOnly />
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  Residual risk
                </div>
                <HrnReadout
                  lo={r.lo_res} fe={r.fe_res} dph={r.dph_res} np={r.np_res}
                  band={bandRes} hrn={hrnRes}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
